import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Input } from '@shigo-ui/ui';
import { useId } from 'react';

import { dashboardT } from '../../../../lib/messages';
import type { TargetingFormValue } from '../../model';
import type { TargetingFormUpdate } from './TargetingForm.types';
import { TargetingSection } from './TargetingSection';

export function PerformanceTargetingSection({
  errors,
  submitted,
  updateField,
  value,
}: {
  errors?: Partial<Record<'ctr' | 'viewability', string>> | undefined;
  submitted: boolean;
  updateField: TargetingFormUpdate;
  value: TargetingFormValue;
}) {
  const viewabilityId = useId();
  const viewabilityUnitId = `${viewabilityId}-unit`;
  const viewabilityErrorId = `${viewabilityId}-error`;
  const ctrId = useId();
  const ctrUnitId = `${ctrId}-unit`;
  const ctrErrorId = `${ctrId}-error`;
  const viewabilityError = submitted ? errors?.viewability : undefined;
  const ctrError = submitted ? errors?.ctr : undefined;
  return (
    <TargetingSection title={dashboardT('targeting.performanceThresholds')}>
      <div className="grid gap-4 md:grid-cols-2">
        <DashboardPageFormField
          label={dashboardT('targeting.viewability')}
          htmlFor={viewabilityId}
          error={
            viewabilityError ? <span id={viewabilityErrorId}>{viewabilityError}</span> : undefined
          }
        >
          <div className="relative">
            <Input
              id={viewabilityId}
              aria-label={dashboardT('targeting.viewability')}
              aria-invalid={Boolean(viewabilityError)}
              aria-describedby={
                viewabilityError ? `${viewabilityUnitId} ${viewabilityErrorId}` : viewabilityUnitId
              }
              className="pr-8"
              inputMode="decimal"
              value={value.viewability}
              onChange={(event) => {
                updateField('viewability', event.target.value);
              }}
            />
            <span
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm"
            >
              %
            </span>
            <span id={viewabilityUnitId} className="sr-only">
              {dashboardT('targeting.percentUnit')}
            </span>
          </div>
        </DashboardPageFormField>
        <DashboardPageFormField
          label={dashboardT('targeting.ctr')}
          htmlFor={ctrId}
          error={ctrError ? <span id={ctrErrorId}>{ctrError}</span> : undefined}
        >
          <div className="relative">
            <Input
              id={ctrId}
              aria-label={dashboardT('targeting.ctr')}
              aria-invalid={Boolean(ctrError)}
              aria-describedby={ctrError ? `${ctrUnitId} ${ctrErrorId}` : ctrUnitId}
              className="pr-8"
              inputMode="decimal"
              value={value.ctr}
              onChange={(event) => {
                updateField('ctr', event.target.value);
              }}
            />
            <span
              aria-hidden="true"
              className="text-muted-foreground pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm"
            >
              %
            </span>
            <span id={ctrUnitId} className="sr-only">
              {dashboardT('targeting.percentUnit')}
            </span>
          </div>
        </DashboardPageFormField>
      </div>
    </TargetingSection>
  );
}
